import React, { useState } from 'react';

interface AWSCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  userName?: string;
  userArn?: string;
}

interface CredentialUploaderProps {
  onCredentialsLoaded: (credentials: AWSCredentials) => void;
}

export const CredentialUploader: React.FC<CredentialUploaderProps> = ({ onCredentialsLoaded }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const parseCSV = (csvText: string): AWSCredentials | null => {
    try {
      const lines = csvText.trim().split('\n');

      // AWS credentials CSV format (downloaded from AWS Console):
      // User name,Password,Access key ID,Secret access key,Console login link
      // OR just: Access key ID,Secret access key

      if (lines.length < 2) {
        throw new Error('CSV file appears to be empty');
      }

      // Check if it has headers
      const firstLine = lines[0].toLowerCase();
      const hasHeaders = firstLine.includes('access key') || firstLine.includes('secret');
      const dataLine = hasHeaders ? lines[1] : lines[0];

      // Parse the data line
      const values = dataLine.split(',').map(v => v.trim().replace(/^"/, '').replace(/"$/, ''));

      let accessKeyId: string | undefined;
      let secretAccessKey: string | undefined;
      let userName: string | undefined;

      if (hasHeaders && firstLine.includes('user name')) {
        // Full AWS format with username
        userName = values[0];
        accessKeyId = values[2];
        secretAccessKey = values[3];
      } else if (values.length >= 2) {
        // Simple format or no headers
        // Try to detect which is which by pattern
        const [first, second] = values;

        // Access Key IDs typically start with AKIA and are 20 chars
        if (first.startsWith('AKIA') && first.length === 20) {
          accessKeyId = first;
          secretAccessKey = second;
        } else if (second.startsWith('AKIA') && second.length === 20) {
          accessKeyId = second;
          secretAccessKey = first;
        } else {
          // Assume first is access key, second is secret
          accessKeyId = first;
          secretAccessKey = second;
        }
      }

      if (!accessKeyId || !secretAccessKey) {
        throw new Error('Could not find Access Key ID and Secret Access Key in CSV');
      }

      // Validate format
      if (!accessKeyId.match(/^[A-Z0-9]{20}$/)) {
        throw new Error('Invalid Access Key ID format (should be 20 uppercase letters/numbers)');
      }

      if (!secretAccessKey.match(/^[A-Za-z0-9+/]{40}$/)) {
        throw new Error('Invalid Secret Access Key format (should be 40 characters)');
      }

      return {
        accessKeyId,
        secretAccessKey,
        userName
      };
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const handleFile = (file: File) => {
    setError(null);
    setSuccess(false);

    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const credentials = parseCSV(text);

      if (credentials) {
        onCredentialsLoaded(credentials);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    };

    reader.onerror = () => {
      setError('Failed to read file');
    };

    reader.readAsText(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  return (
    <div className="mb-6">
      <div className="flex items-center gap-4 mb-4">
        <h3 className="text-lg font-semibold">📁 Upload Credentials</h3>
        {success && (
          <span className="text-green-400 text-sm animate-pulse">
            ✅ Credentials loaded successfully!
          </span>
        )}
      </div>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-all
          ${isDragging
            ? 'border-blue-500 bg-blue-500/10'
            : 'border-gray-600 hover:border-gray-500'
          }
        `}
      >
        <div className="flex flex-col items-center gap-3">
          <svg
            className="w-12 h-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>

          <div>
            <p className="text-white mb-1">
              Drop your AWS credentials CSV here
            </p>
            <p className="text-sm text-gray-400">
              or click to browse
            </p>
          </div>

          <input
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
            id="csv-upload"
          />
          <label
            htmlFor="csv-upload"
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded cursor-pointer transition"
          >
            Choose CSV File
          </label>
        </div>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-900/20 border border-red-600 rounded">
          <p className="text-red-400 text-sm">❌ {error}</p>
        </div>
      )}

      <div className="mt-4 p-4 bg-blue-900/20 border border-blue-600 rounded">
        <h4 className="font-semibold text-blue-400 mb-2">📋 Supported CSV Formats:</h4>
        <div className="space-y-2 text-sm text-gray-300">
          <div>
            <p className="font-medium">1. AWS Console Download Format:</p>
            <code className="block bg-black/30 p-2 rounded mt-1 text-xs">
              User name,Password,Access key ID,Secret access key,Console login link<br/>
              b@oh.mom,,AKIAIOSFODNN7EXAMPLE,wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY,https://...
            </code>
          </div>
          <div>
            <p className="font-medium">2. Simple Format:</p>
            <code className="block bg-black/30 p-2 rounded mt-1 text-xs">
              Access key ID,Secret access key<br/>
              AKIAIOSFODNN7EXAMPLE,wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY
            </code>
          </div>
          <div>
            <p className="font-medium">3. No Headers:</p>
            <code className="block bg-black/30 p-2 rounded mt-1 text-xs">
              AKIAIOSFODNN7EXAMPLE,wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY
            </code>
          </div>
        </div>
      </div>

      <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-600 rounded">
        <p className="text-yellow-400 text-sm">
          ⚠️ <strong>Security Note:</strong> The CSV file is processed locally in your browser.
          No file data is uploaded to any server until you click "Validate & Save".
        </p>
      </div>
    </div>
  );
};